import { AdAccountsPayload, GetAdAccountsResponse } from "../interfaces/api/get-ad-accounts";
import { Ad, AdsPayload, GetAdsResponse } from "../interfaces/api/get-ads";
import { Adset, AdsetsPayload, GetAdsetsResponse } from "../interfaces/api/get-adsets";
import {
  Campaign,
  CampaignsPayload,
  GetCampaignsResponse,
} from "../interfaces/api/get-campaigns";
import { DeliveryEstimatePayload, GetDeliveryEstimateResponse } from "../interfaces/api/get-delivery-estimate";
import { GetInterestsResponse, InterestsPayload } from "../interfaces/api/get-interests";
import { GetLanguagesResponse, LanguagesPayload } from "../interfaces/api/get-languages";
import { GetLocationsResponse, LocationsPayload } from "../interfaces/api/get-locations";
import { GetMinBudgetResponse, MinBudgetPayload } from "../interfaces/api/get-min-budgets";
import { PutAdRequest, PutAdResponse } from "../interfaces/api/put-ad";
import { PutAdsetRequest, PutAdsetResponse } from "../interfaces/api/put-adset";
import { PutCampaignRequest, PutCampaignResponse } from "../interfaces/api/put-campaign";

function pickOptionalNumber(raw: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string" && v.trim() !== "") {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return undefined;
}

function pickImageUrl(raw: Record<string, unknown>): string | undefined {
  const keys = [
    "imageUrl",
    "image_url",
    "thumbnailUrl",
    "thumbnail_url",
    "picture",
    "pictureUrl",
    "previewUrl",
    "preview_url",
    "url",
    "full_picture",
    "permalink_url",
  ];
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string" && v.trim().length > 0) {
      return v.trim();
    }
  }
  return undefined;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

/** URLs en raíz o dentro de creative / object_story_spec (Marketing API). */
function pickImageUrlDeep(raw: Record<string, unknown>): string | undefined {
  const flat = pickImageUrl(raw);
  if (flat) return flat;

  const creative = asRecord(raw.creative);
  if (creative) {
    const fromCreative = pickImageUrl(creative);
    if (fromCreative) return fromCreative;
    const img = creative.image_url ?? creative.thumbnail_url;
    if (typeof img === "string" && img.trim()) return img.trim();
    const oss = asRecord(creative.object_story_spec);
    if (oss) {
      const fromOss = pickImageFromObjectStorySpec(oss);
      if (fromOss) return fromOss;
    }
  }

  const eff = asRecord(raw.effective_object_story_spec);
  if (eff) {
    const fromEff = pickImageFromObjectStorySpec(eff);
    if (fromEff) return fromEff;
  }

  const oss = asRecord(raw.object_story_spec);
  if (oss) {
    const fromOss = pickImageFromObjectStorySpec(oss);
    if (fromOss) return fromOss;
  }

  return undefined;
}

function pickImageFromObjectStorySpec(
  oss: Record<string, unknown>
): string | undefined {
  const linkData = asRecord(oss.link_data);
  if (linkData) {
    const pic = linkData.picture ?? linkData.image_url;
    if (typeof pic === "string" && pic.trim()) return pic.trim();
  }
  const videoData = asRecord(oss.video_data);
  if (videoData) {
    const img = videoData.image_url ?? videoData.picture;
    if (typeof img === "string" && img.trim()) return img.trim();
  }
  const photoData = asRecord(oss.photo_data);
  if (photoData) {
    const img = photoData.url ?? photoData.image_url;
    if (typeof img === "string" && img.trim()) return img.trim();
  }
  return undefined;
}

function normalizeCampaignsList(response: unknown): unknown[] {
  const r = asRecord(response);
  if (!r) return [];
  if (Array.isArray(r.campaigns)) return r.campaigns;
  const payload = asRecord(r.payload);
  if (payload) {
    if (Array.isArray(payload.campaigns)) return payload.campaigns;
    if (Array.isArray(payload.data)) return payload.data;
  }
  return [];
}

function normalizeAdsetsList(response: unknown): unknown[] {
  const r = asRecord(response);
  if (!r) return [];
  if (Array.isArray(r.adsets)) return r.adsets;
  const payload = asRecord(r.payload);
  if (payload) {
    if (Array.isArray(payload.adsets)) return payload.adsets;
    if (Array.isArray(payload.data)) return payload.data;
  }
  return [];
}

function normalizeAdsList(response: unknown): unknown[] {
  const r = asRecord(response);
  if (!r) return [];
  if (Array.isArray(r)) return r;
  if (Array.isArray(r.ads)) return r.ads;
  const data = asRecord(r.data);
  if (data) {
    if (Array.isArray(data.ads)) return data.ads;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.records)) return data.records;
    if (Array.isArray(data)) return data as unknown[];
  }
  const payload = asRecord(r.payload);
  if (payload) {
    if (Array.isArray(payload)) return payload as unknown[];
    if (Array.isArray(payload.ads)) return payload.ads;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.records)) return payload.records;
    if (Array.isArray(payload.list)) return payload.list;
  }
  return [];
}

/** En consola: `localStorage.setItem('DEBUG_ADS','1')` y recarga; al expandir un conjunto verás el cuerpo crudo y el recuento normalizado. */
function debugAdsPayload(stage: string, response: unknown, list: unknown[]) {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem("DEBUG_ADS") !== "1") return;
  // eslint-disable-next-line no-console
  console.info(`[Ads][${stage}]`, {
    normalizedCount: list.length,
    topKeys:
      response && typeof response === "object"
        ? Object.keys(response as object)
        : [],
    rawSample: list[0],
  });
}

function pickOptionalString(
  raw: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
    if (typeof v === "number" && !Number.isNaN(v)) return String(v);
  }
  return undefined;
}

function entityIdString(r: Record<string, unknown>): string {
  const s = pickOptionalString(r, ["id"]);
  if (s) return s;
  return "";
}

export const advertisementsMapper = {
  mapResponseToAdAccounts(
    response: GetAdAccountsResponse
  ): AdAccountsPayload {
    return { adAccounts: response.payload.adAccounts };
  },
  mapResponseToLocations(
    response: GetLocationsResponse
  ): LocationsPayload {
    return { locations: response.payload.locations };
  },
  mapResponseToInterests(
    response: GetInterestsResponse
  ): InterestsPayload {
    return { interests: response.payload.interests };
  },
  mapResponseToLanguages(
    response: GetLanguagesResponse
  ): LanguagesPayload {
    return { languages: response.payload.languages };
  },
  mapResponseToCampaigns(
    response: GetCampaignsResponse
  ): CampaignsPayload {
    const campaigns = normalizeCampaignsList(response).map((raw) => {
      const r = raw as unknown as Record<string, unknown>;
      const imageUrl = pickImageUrlDeep(r);
      const conversions = pickOptionalNumber(r, [
        "conversions",
        "conversions_count",
        "conversion_count",
      ]);
      const conversionRate = pickOptionalNumber(r, [
        "conversionRate",
        "conversion_rate",
      ]);
      const id = entityIdString(r);
      return {
        ...(raw as Campaign),
        id,
        ...(imageUrl ? { imageUrl } : {}),
        ...(conversions != null ? { conversions } : {}),
        ...(conversionRate != null ? { conversionRate } : {}),
      };
    });
    return { campaigns };
  },
  mapResponseToAdsets(
    response: GetAdsetsResponse
  ): AdsetsPayload {
    const adsets = normalizeAdsetsList(response).map((raw) => {
      const r = raw as unknown as Record<string, unknown>;
      const imageUrl = pickImageUrlDeep(r);
      const id = entityIdString(r);
      return {
        ...(raw as Adset),
        id,
        ...(imageUrl ? { imageUrl } : {}),
      };
    });
    return { adsets };
  },
  mapResponseToAds(
    response: GetAdsResponse,
    adsetIdFromRequest?: string
  ): AdsPayload {
    const rawList = normalizeAdsList(response);
    debugAdsPayload("mapResponseToAds", response, rawList);
    const ads = rawList.map((raw) => {
      const r = raw as unknown as Record<string, unknown>;
      const imageUrl = pickImageUrlDeep(r);
      const id =
        pickOptionalString(r, ["id", "ad_id", "adId"]) ?? entityIdString(r);
      /** Siempre el id del query param (misma cadena que en la UI) para evitar desajustes con el API. */
      const adsetId =
        adsetIdFromRequest != null && String(adsetIdFromRequest).length > 0
          ? String(adsetIdFromRequest)
          : pickOptionalString(r, ["adset_id", "adsetId", "adset"]);
      const objective = pickOptionalString(r, [
        "objective",
        "object_type",
        "format",
        "creative_type",
      ]);
      return {
        ...(raw as Ad),
        id,
        ...(objective ? { objective } : {}),
        ...(imageUrl ? { imageUrl } : {}),
        ...(adsetId ? { adsetId } : {}),
      };
    });
    return { ads };
  },
  mapResponseToCampaign(
    response: PutCampaignResponse
  ): PutCampaignRequest {
    return { campaign: response.payload.campaign, status: response.payload.status }
  },
  mapResponseToDeliveryEstimate(
    response: GetDeliveryEstimateResponse
  ): DeliveryEstimatePayload {
    return { estimate: response.payload.estimate }
  },
  mapResponseToAd(
    response: PutAdResponse
  ): PutAdRequest {
    return { ad: response.payload.ad, status: response.payload.status }
  },
  mapResponseToAdset(
    response: PutAdsetResponse
  ): PutAdsetRequest {
    return { 
      adset: response.payload.adset, 
      status: response.payload.status, 
      dailyBudget: response.payload.dailyBudget 
    }
  },
  mapResponseToMinBudget(
    response: GetMinBudgetResponse
  ): MinBudgetPayload {
    return { minBudget: response.payload.minBudget };
  },
};
